<template>
  <div>
    <Dialog :open="open" @update:open="emitClose">
      <DialogContent class="sm:max-w-[500px]">
        <div v-if="isLoading">
          <LayupSpinner />
        </div>
        <div v-else>
          <DialogHeader>
            <DialogTitle>Delete Card</DialogTitle>
            <DialogDescription>
              Card No: <strong>**** **** **** {{ cardNumber }}</strong>
            </DialogDescription>
          </DialogHeader>

          <div class="flex flex-col gap-4 py-4">
            <p class="text-xl">Are you sure you want to delete this card?</p>
          </div>

          <DialogFooter>
            <div class="flex justify-end gap-4 mt-4 mr-8">
              <Button type="button" variant="outline" @click="emitClose">Cancel</Button>
              <Button class="w-auto bg-red-600 hover:bg-red-700" :disabled="!cardId" @click="onSubmit">Yes</Button>
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { NetworkError } from '@/errors';
import { deleteCard } from '@/services/cards';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { ref } from 'vue';
import { useToast } from 'vue-toast-notification';

const props = defineProps({
  cardNumber: String,
  cardId: String,
  open: Boolean,
});

const errorMessage = ref('');
const isLoading = ref(false);

const emit = defineEmits(['update:open', 'update:get-cards']);
const toast = useToast();

const emitClose = () => {
  emit('update:open', false);
};

const onSubmit = async () => {
  try {
    isLoading.value = true;
    await deleteCard(props.cardId!);
    await emit('update:get-cards');
    toast.success('Card was removed successfully!', { position: 'top' });
  } catch (error: any) {
    errorMessage.value = 'Something went wrong. Please try again later.';
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('billing-and-fees-page', {
        status: 'network_error',
        channel: 'merchant',
        method: 'updateMerchantUser:onAddMerchant',
      });
    } else {
      createException(error);
      addMetric('billing-and-fees-page', {
        status: 'error',
        channel: 'merchant',
        method: 'updateMerchantUser:onAddMerchant',
      });
    }
    if (error.response.data) {
      errorMessage.value = error.response.data;
    }
  } finally {
    emitClose();
    isLoading.value = false;
  }
};
</script>
